import { isLetter, isNumber } from "./strings"

export function sum(arr: number[]): number
export function sum<T>(arr: T[], fn: (ele: T) => number): number
export function sum(arr: any[], fn?: (ele: any) => number) {
  let total = 0
  if (fn) {
    for (const ele of arr) {
      total += fn(ele)
    }
  } else {
    for (const ele of arr) {
      total += ele
    }
  }
  return total
}

export function sortBy<T>(arr: T[], fn: (ele: T) => any) {
  return arr
    .map(ele => ({ ele, val: fn(ele) }))
    .sort((a, b) => (a.val < b.val ? -1 : a.val > b.val ? 1 : 0))
    .map(({ ele }) => ele)
}

// Sort are string array by the first character that is different
export function sortByFirstDiffChunk(a: string, b: string) {
  const aChunk = replaceNumbers(splitStringIntoChunks(a).map(str => str.toLowerCase()))
  const bChunk = replaceNumbers(splitStringIntoChunks(b).map(str => str.toLowerCase()))

  for (let i = 0; i < aChunk.length; i++) {
    if (aChunk[i] !== bChunk[i]) {
      return aChunk[i] < bChunk[i] ? -1 : 1
    }
  }
  return 0
}

// replace strings that are numbers in an array with numbers
export function replaceNumbers(arr: string[]) {
  return arr.map(ele => {
    if (isNumber(ele)) {
      return parseInt(ele, 10)
    } else {
      return ele
    }
  })
}

// split string into chunks of letters and numbers
function splitStringIntoChunks(str = "") {
  if (str.length === 0) {
    return []
  }
  if (str.length === 1) {
    return [str]
  }
  const chunks = [str[0]]
  // loop through string
  for (let i = 1; i < str.length; i++) {
    // if the previous character is a number and the current character is a letter
    if ((isNumber(str[i - 1]) && isLetter(str[i])) || (isLetter(str[i - 1]) && isNumber(str[i]))) {
      chunks.push(str[i])
    }
    // if the previous chunk contains a letter and the current character is a letter
    else if (isLetter(chunks[chunks.length - 1]) && isLetter(str[i])) {
      chunks[chunks.length - 1] += str[i]
    }
    // if the previous chunk contains a number and the current character is a number
    else if (isNumber(chunks[chunks.length - 1]) && isNumber(str[i])) {
      chunks[chunks.length - 1] += str[i]
    }
  }
  return chunks
}

// splice array and return new array
// with no value this only removes - passing undefined through to splice would insert an undefined item
export function spliceArray(array: any[], index: number, count: number, value?: any) {
  const newArray = [...array]
  if (arguments.length > 3) {
    newArray.splice(index, count, value)
  } else {
    newArray.splice(index, count)
  }
  return newArray
}

// slice array and return new array
export function sliceArray(array: any[], start: number, end?: number) {
  const newArray = [...array]
  newArray.splice(start, end)
  return newArray
}

// Total value of items with a value and quantity
export function totalValue(items: { value: number; quantity: number }[]) {
  return items.reduce((total, item) => total + item.value * item.quantity, 0)
}

// Batch run an async function
export async function batchRun(
  items: any[],
  batchSize: number,
  fn: (items: any[], run: number, runs: number) => Promise<any>,
) {
  const batches = chunk(items, batchSize)
  for (const batch of batches) {
    await fn(batch, batches.indexOf(batch) + 1, batches.length)
  }
}

// Chunk an array into smaller arrays
export function chunk<T>(array: T[], size: number) {
  const chunked_arr = []
  let index = 0
  while (index < array.length) {
    chunked_arr.push(array.slice(index, size + index))
    index += size
  }
  return chunked_arr
}

export function toggleArray<T>(arr: T[], el: T) {
  return arr.includes(el) ? arr.filter(ele => ele !== el) : [...arr, el]
}

export function arrayRemove<T>(arr: T[], el: T) {
  return arr.includes(el) ? arr.filter(ele => ele !== el) : arr
}

export function arraySub<T>(arr: T[], toRemove: T[]) {
  return toRemove.reduce((acc, ele) => arrayRemove(acc, ele), arr)
}

export function pushIfNeeded<T>(arr: T[], el: T) {
  return arr.includes(el) ? arr : [...arr, el]
}

/**
 * compare two numbers with a margin of error. returns comparison like (valueA === valueB)
 * @param valueA value to compare
 * @param valueB value to compare
 * @param margin margin of error
 * @returns true if value a and value b are within the margin of error
 */
export function fuzzyNumberCompare(
  valueA?: number | null,
  valueB?: number | null,
  margin?: number | null,
) {
  if (typeof valueA !== typeof valueB) {
    return false
  }
  if (valueA === null || valueB === null || valueA === undefined || valueB === undefined) {
    return true
  }
  const marginV = margin ?? 0
  return Math.abs(valueA - valueB) <= marginV
}
